// src/services/pageFollow.js — following a page, and who is following it.
//
// 🟢 A CASE-FOR-CASE PORT OF allo-app/services/pageFollow.ts. There is one
//    follow relationship in the whole product — `pages/{id}.followers`, a list
//    of uids with `followersCount` beside it — and Allo's own account is a page
//    (`pages/allo_official`), which is why "show the follower count for all
//    pages" is one piece of work rather than a feature plus a special case.
//
// 🟥 THE LIST IS THE TRUTH; THE COUNT IS A HINT. `followersCount` is written
//    with increment(), and an increment that runs twice leaves a number nobody
//    can reconcile. Anything that can afford to count the array counts it.
//
// 🟥 AND THE PRIVACY RULE HAS TO MATCH THE PHONE'S EXACTLY. Two clients
//    disagreeing here does not look like a bug: it looks like the web exposing
//    somebody who chose to be hidden on their phone.
import { doc, getDoc, updateDoc, arrayUnion, arrayRemove, increment } from 'firebase/firestore';
import { db } from '../config/firebase';

/** The page that is Allo. Matches OFFICIAL_PAGE_ID on the server. */
export const OFFICIAL_PAGE_ID = 'allo_official';

export const FOLLOWER_PAGE = 40;

/** Does this page carry the blue check? Read the page, never the name. */
export function isOfficialPage(page) {
  return !!page && (page.isOfficial === true || page.id === OFFICIAL_PAGE_ID);
}

/** The number to show. The array when we have it, the stored count otherwise. */
export function followerCountOf(page) {
  if (Array.isArray(page?.followers)) return page.followers.length;
  return Number(page?.followersCount) || 0;
}

/** Am I following this page? */
export function amIFollowing(page, uid) {
  return !!uid && Array.isArray(page?.followers) && page.followers.includes(uid);
}

/**
 * May `viewerUid` see `ownerUid` in a public follower list?
 *
 * 🟥 AN UNRECOGNISED SETTING READS AS 'everyone', matching every other privacy
 *    field in this product. Failing closed on a typo would silently empty the
 *    follower list of everybody who has never opened the privacy screen — which
 *    looks like a page with no followers rather than like a bug.
 * 🟢 I ALWAYS SEE MYSELF, so somebody who has hidden themselves can still tell
 *    that their follow worked.
 */
export function canSeeInFollowerList({ viewerUid, ownerUid, ownerData }) {
  if (!ownerUid) return false;
  if (viewerUid && viewerUid === ownerUid) return true;
  const vis = ownerData?.privacySettings?.followsVisibility || 'everyone';
  return vis !== 'nobody';
}

/**
 * Follow or unfollow, and hand back the state it is now in.
 *
 * 🟥 THE COUNT MOVES WITH THE LIST OR NOT AT ALL. Writing one without the other
 *    is what makes a page say "3 followers" over a list of five names; the
 *    rules refuse it too, so a client that got this wrong would fail the write
 *    rather than quietly drift.
 */
/**
 * ➕ Is this page already "followed" as far as a Follow button is concerned —
 * followed by me, or mine? null when it cannot be read (try again later).
 */
export async function pageFollowedBy(pageId, uid) {
  if (!pageId || !uid) return null;
  const snap = await getDoc(doc(db, 'pages', pageId));
  const pg = snap.exists() ? snap.data() : null;
  if (!pg) return null;
  return amIFollowing(pg, uid) || pg.ownerUid === uid;
}

export async function toggleFollow(pageId, uid, currentlyFollowing) {
  if (!pageId || !uid) return { ok: false, following: currentlyFollowing };
  try {
    await updateDoc(doc(db, 'pages', pageId), currentlyFollowing
      ? { followers: arrayRemove(uid), followersCount: increment(-1) }
      : { followers: arrayUnion(uid), followersCount: increment(1) });
    return { ok: true, following: !currentlyFollowing };
  } catch (e) {
    console.warn('[pageFollow] toggle failed:', e?.message);
    return { ok: false, following: currentlyFollowing };
  }
}

/**
 * One page of followers, newest first, profiles filled in, hidden ones removed.
 *
 * 🟢 NEWEST FIRST because arrayUnion appends, so the end of the list is the
 *    recent end — and a follower list is opened to see who has just arrived.
 * 🟥 AN UNREADABLE PROFILE IS NOT A VISIBLE ONE. A bare uid would put a
 *    nameless row in the list opening a profile that is not there, and would
 *    show somebody whose privacy setting could not be checked.
 */
export async function loadFollowers(page, viewerUid, offset = 0, pageSize = FOLLOWER_PAGE) {
  const all = (Array.isArray(page?.followers) ? page.followers : []).filter(Boolean);
  const ordered = [...all].reverse();
  const slice = ordered.slice(offset, offset + pageSize);

  const rows = await Promise.all(slice.map(async (uid) => {
    try {
      const snap = await getDoc(doc(db, 'users', String(uid)));
      if (!snap.exists()) return null;
      const u = snap.data() || {};
      if (!canSeeInFollowerList({ viewerUid, ownerUid: String(uid), ownerData: u })) return null;
      return {
        uid: String(uid),
        fullName: u.fullName || u.username || '',
        username: u.username || '',
        profileImage: u.profileImage || '',
      };
    } catch {
      return null;
    }
  }));

  const next = offset + slice.length;
  return {
    rows: rows.filter(Boolean),
    nextOffset: next < ordered.length ? next : null,
    total: ordered.length,
  };
}
